import { THEME_IDS } from '@whistler-ui/k2/constants';
import { PropsWithChildren } from 'react';

import { useAuthStore } from '../store';
import { useThemeStore } from '../store/theme';
import { getSiteRegion } from './utils/getEnv';

interface Props {
  ues?: boolean;
  type?: string[] | string;
  isTrue?: boolean;
  onlyUs?: boolean;
  onlyNonUs?: boolean;
  disabledOrganisationIds?: string[];
  allowedOrganisationIds?: string[];
  [key: string]: any;
}

interface RenderIfProps extends PropsWithChildren, Props {}

export const useRenderIf = () => {
  const {
    authValues: {
      organisationState: { organisation },
    },
  } = useAuthStore();
  const { config } = useThemeStore();

  const organisation_ues = organisation?.ues;
  const organisation_type = organisation?.type;
  const organisation_id = organisation?.id;

  const isGD = config.context === THEME_IDS.GD;
  const isNL = config.context === THEME_IDS.NETLYNC;
  const isCSG = config.context === THEME_IDS.CSG;
  const isGSMA = config.context === THEME_IDS.GSMA;

  const partnersList = [
    { condition: isCSG, module: 'csg' },
    { condition: isNL, module: 'common' },
    { condition: isGD, module: 'common' },
    { condition: isGSMA, module: 'gsma' },
  ];

  const isUsRegion = () => {
    const region = getSiteRegion();

    return region && region.includes('us');
  };
  const conditions = [
    ({ ues }: Props) => {
      if (typeof ues === 'undefined') {
        return false;
      }
      return Boolean(ues) === Boolean(organisation_ues);
    },
    ({ type = [] }: { type?: string[] | string }) => {
      if (type.length === 0) {
        return false;
      }

      return type.includes(organisation_type);
    },
    ({ isTrue }: Props) => {
      return Boolean(isTrue);
    },
    ({ onlyUs }: Props) => {
      if (typeof onlyUs === 'undefined') {
        return false;
      }
      return isUsRegion();
    },
    ({ onlyNonUs }: Props) => {
      if (typeof onlyNonUs === 'undefined') {
        return false;
      }

      return !isUsRegion();
    },

    ({ allowedOrganisationIds }: Props) => {
      if (
        typeof allowedOrganisationIds === 'undefined' ||
        allowedOrganisationIds?.length < 1
      ) {
        return false;
      }

      return allowedOrganisationIds.includes(organisation_id);
    },
    ({ disabledOrganisationIds }: Props) => {
      if (
        typeof disabledOrganisationIds === 'undefined' ||
        disabledOrganisationIds?.length < 1
      ) {
        return false;
      }

      return !disabledOrganisationIds.includes(organisation_id);
    },
  ];

  const shouldRender = (props: Props): boolean => {
    return conditions.some(condition => condition(props));
  };

  const RenderIf = ({ children, ...props }: RenderIfProps) => {
    const result = shouldRender(props);
    return result ? children : null;
  };

  const activePartner =
    partnersList.find(partner => partner.condition)?.module || null;

  return {
    RenderIf,
    shouldRender,
    isUsRegion,
    isUES: organisation_ues,
    orgType: organisation_type,
    isGD,
    isNL,
    isCSG,
    isGSMA,
    activePartner,
    partnerName: config.context,
  };
};

export default useRenderIf;
